import React, { useState, useMemo, useEffect } from "react";
import HeaderComponents from "../../components/HeaderComponents";
import "../../utils/Style/pages/Overview.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { useSortBy, useTable } from "react-table";
import Table from "../../components/Table";
import { capitalizeFirstLetters } from "../../features/ValueFormater";
import "../../utils/Style/pages/AwaitingCourier.scss"
import DatePicker from "../../components/DatePicker";
import useAwaitingCourier from "../../api/AwaitingCourierApi";
import TransferListSkeleton from "../../components/skeletons/TransferListSkeleton";
import UtcToLocal from "../../components/UtcToLocal";
import { useDispatch } from "react-redux";
import { setActionPayload, setActionType } from "../../features/authorizationSlice";
import { ACTION_TYPES } from "../../app/actionTypes";
import { useOutletContext } from "react-router-dom";
import useOrderProcessingApi from "../../api/OrderProcessingApi";
import Pagination from "../../components/Pagination";
import RecordsMessage from "../../components/RecordsMessage";

function AwaitingCourier({ method }) {
    const [activeRowIndex, setActiveRowIndex] = useState(-1);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedPage, setSelectedPage] = useState(1);
    const [orders, setOrders] = useState([]);
    const [selectedOrderType, setSelectedOrderType] = useState("");
    const dispatch = useDispatch();
    const [onOpenQrAfterGenerate] = useOutletContext();

    const { GetOrderTypes, orderTypes } = useOrderProcessingApi();
    const { GetAwaitOrders, awaitOrdersLoading, awaitorders, totalCount, totalNbOfPage } = useAwaitingCourier();

    useEffect(() => {
        GetOrderTypes();
    }, [])

    useEffect(() => {
        if (orderTypes) {
            setOrders(orderTypes);
        }
    }, [orderTypes]);

    const [dateRange, setDateRange] = useState([
        new Date(new Date().setMonth(new Date().getMonth() - 1)),
        new Date(),
    ]);

    const [dateString, setDateString] = useState({
        startDateString: "",
        endDateString: ""
    });

    useEffect(() => {
        if (dateRange && dateRange.length === 2) {
            const [start] = dateRange;
            const end = new Date();

            if (start && !isNaN(start) && !isNaN(end)) {
                const startDate = new Date(Date.UTC(start.getUTCFullYear() - 1, start.getUTCMonth(), start.getUTCDate()));
                const endDate = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));

                const startDateString = startDate.toISOString().substring(0, 10);
                const endDateString = endDate.toISOString().substring(0, 10);

                setDateString({
                    startDateString,
                    endDateString
                });

                GetAwaitOrders(1, startDateString, endDateString, 0);
            }
        }
    }, [dateRange]);

    const options = {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
    };

    const handleCancel = (orderId) => {
        const payload = {
            orderId: orderId
        };
        dispatch(setActionType(ACTION_TYPES.CANCEL_ORDER));
        dispatch(setActionPayload(payload));
        onOpenQrAfterGenerate();
    };

    const columns = useMemo(() => [
        {
            Header: "Order ID",
            accessor: "orderID",
        },
        {
            Header: "Type",
            accessor: "type",
            Cell: ({ row, value }) => {
                const type = row.original.type;
                if (type === "Withdraw") {
                    return (
                        <div>
                            <img src="/Images/triangle.svg" className="triangle-icon" alt="Triangle Icon" />
                            {value}
                        </div>
                    )
                } else {
                    return (
                        <div>
                            <img src="/Images/reverse-triangle.svg" className="triangle-icon" alt="Triangle Icon" />
                            {value}
                        </div>
                    )
                }
            }
        },
        {
            Header: "",
            accessor: "details",
            Cell: ({ row }) => (
                <button className="showmore" onClick={() => setActiveRowIndex(row.index)}>
                    <FontAwesomeIcon icon={faArrowDown} />
                </button>
            ),
        },
        {
            Header: "User ID",
            accessor: "userID",
        },
        {
            Header: "Currency",
            accessor: "currency",
        },
        {
            Header: "Amount",
            accessor: "amount",
            Cell: ({ cell: { value, row } }) => {
                const currencySymbol = row.original.currency === "USD" ? `$ ${value}` : `${value} L.L`;
                return (
                    <div style={{ fontFamily: "Poppins SemiBold" }}>
                        {currencySymbol}
                    </div>
                );
            }
        },
        {
            Header: "Date",
            accessor: "date",
            Cell: ({ cell: { value } }) => {
                return <UtcToLocal utcTimestamp={value} options={options} />
            },
        },
        {
            Header: "Driver Name",
            accessor: "driverName",
        },
        {
            Header: "Time",
            id: "time",
            accessor: "date",
            Cell: ({ cell: { value } }) => {
                const date = new Date(value);
                return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            },
        },
        {
            Header: "Status",
            accessor: "status",
            Cell: ({ cell: { value } }) => {
                if (["new", "out of delivery", "cancelled by client"].includes(value.toLowerCase())) {
                    return <div className={"status " + value?.toUpperCase()}>{capitalizeFirstLetters(value)}</div>
                }
            }
        },
        {
            Header: "",
            accessor: "delete",
            Cell: ({ row }) => {
                const role = sessionStorage.getItem("Role")
                // if(role === "Employee") 
                return (
                    <button className="cancel-btn" onClick={() => handleCancel(row.original.orderID)}>
                        Cancel
                    </button>
                )
            }
        },
    ], []);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable({ columns, data: awaitorders }, useSortBy);

    const filteredRows = rows.filter((row) => {
        const orderTypeMatch = selectedOrderType === "" || row.original.type === selectedOrderType;
        const searchQueryMatch = Object.values(row.original).some((cellValue) =>
            cellValue && cellValue.toString().toLowerCase().includes(searchQuery.toLowerCase())
        );
        return orderTypeMatch && searchQueryMatch;
    });

    return (
        <>
            <div className="AwaitingCourierPage">
                <HeaderComponents title="Awaiting Courier" />
                <div className="AwaitingCourierPageContainer">
                    <div className="table-container">
                        <div className="tableTop">
                            <RecordsMessage
                                totalCount={totalCount}
                                selectedPage={selectedPage}
                                records="Orders"
                            />
                            <div className="filtering">
                                <DatePicker
                                    value={dateRange}
                                    onChange={setDateRange}
                                />
                                <div className="filteringSearch">
                                    <input
                                        className="search"
                                        placeholder="Search"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)} />
                                    <span className="iconSearch"><FontAwesomeIcon icon={faMagnifyingGlass} /></span>
                                </div>
                                <select
                                    className="ddselect"
                                    name="order"
                                    id="order"
                                    value={selectedOrderType}
                                    onChange={(e) => setSelectedOrderType(e.target.value)}
                                >
                                    <option value="">All Orders</option>
                                    {orders.map(order => (
                                        <option key={order.value} value={order.value}>
                                            {order.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="row">
                            {
                                awaitOrdersLoading ? <TransferListSkeleton />
                                    :
                                    <div className={activeRowIndex === -1 ? "col-12" : "col-8"}>
                                        <Table
                                            headerGroups={headerGroups}
                                            page={filteredRows}
                                            prepareRow={prepareRow}
                                            getTableProps={getTableProps}
                                            getTableBodyProps={getTableBodyProps}
                                            activeRowIndex={activeRowIndex}
                                            setActiveRowIndex={setActiveRowIndex}
                                            totalNbPage={totalNbOfPage}
                                            sort={true}
                                        />
                                        <Pagination
                                            getData={GetAwaitOrders}
                                            totalNbPage={totalNbOfPage}
                                            method="awaitOrders"
                                            case={method}
                                            startDateString={dateString.startDateString}
                                            endDateString={dateString.endDateString}
                                            selectedPage={selectedPage}
                                            setSelectedPage={setSelectedPage}
                                        />
                                    </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default AwaitingCourier;
